const {
  useRef,
  useCallback,
  useEffect
} = require('react');

// Used to prevent accidental triggering of dragging on click.
const DRAG_THRESHOLD = 2;

/**
 * Based on: https://htmldom.dev/drag-to-scroll/
 */
const useDragToScroll = getElement => {
  const elementRef = useRef();
  const isDraggingRef = useRef(false);
  const posRef = useRef({
    top: 0,
    left: 0,
    x: 0,
    y: 0
  });
  const onClick = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const onMouseMove = useCallback(event => {
    const dx = event.clientX - posRef.current.x;
    const dy = event.clientY - posRef.current.y;
    const meetsThreshold = dy <= -DRAG_THRESHOLD || dy >= DRAG_THRESHOLD || dx <= -DRAG_THRESHOLD || dx >= DRAG_THRESHOLD;
    if (elementRef.current && (isDraggingRef.current || meetsThreshold)) {
      elementRef.current.scrollLeft = posRef.current.left - dx;
      elementRef.current.scrollTop = posRef.current.top - dy;
      if (!isDraggingRef.current) {
        elementRef.current.style.scrollBehavior = 'auto';
        document.addEventListener('click', onClick, true);
        isDraggingRef.current = true;
      }
    }
  }, [onClick]);
  const onMouseUp = useCallback(() => {
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
    elementRef.current?.style.removeProperty('scroll-behavior');
    setTimeout(() => {
      document.removeEventListener('click', onClick, true);
      isDraggingRef.current = false;
    });
  }, [onMouseMove, onClick]);
  const onMouseDown = useCallback(event => {
    posRef.current = {
      left: elementRef.current?.scrollLeft || 0,
      top: elementRef.current?.scrollTop || 0,
      x: event.clientX,
      y: event.clientY
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [onMouseMove, onMouseUp]);
  useEffect(() => {
    elementRef.current = getElement();
    elementRef.current?.addEventListener('mousedown', onMouseDown);
    return () => {
      elementRef.current?.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('click', onClick, true);
      isDraggingRef.current = false;
      elementRef.current?.style.removeProperty('scroll-behavior');
    };
  }, [getElement, onMouseDown, onMouseMove, onMouseUp, onClick]);
  return elementRef;
};
export default useDragToScroll;